import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import {
  Plus, Trash2, Package, PackageCheck, PackageOpen, ArrowLeftRight, Truck,
  Filter, LayoutGrid, ListChecks, MapPin, User, Users, Sparkles, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "decor", label: "Decor" },
  { value: "rental", label: "Rentals" },
  { value: "food_bev", label: "Food & bev" },
  { value: "av", label: "AV / tech" },
  { value: "signage", label: "Signage" },
  { value: "favor", label: "Favors & gifts" },
  { value: "other", label: "Other" },
];

const emptyForm = {
  name: "", category: "decor", quantity: 1,
  space_id: "", sender_kind: "text", sender_label: "", sender_ref_id: "",
  return_to: "", arrival_window: "", install_notes: "", strike_notes: "",
  responsible_member_id: "",
};

export default function SetupStrikeTab({ eventId, members, canEdit }) {
  const [items, setItems] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [lane, setLane] = useState("setup"); // "setup" | "strike"
  const [groupBySpace, setGroupBySpace] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterSpace, setFilterSpace] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const vendors = useMemo(() => members.filter(m => m.role === "vendor"), [members]);

  const load = useCallback(async () => {
    try {
      const [i, sp, cl] = await Promise.all([
        api.get(`/events/${eventId}/setup-items`),
        api.get(`/events/${eventId}/spaces`).catch(() => ({ data: [] })),
        api.get(`/clients`).catch(() => ({ data: [] })),
      ]);
      setItems(i.data);
      setSpaces(sp.data);
      setClients(cl.data);
    } catch (e) {
      console.error("SetupStrike load failed:", e);
      toast.error("Could not load setup plan");
    } finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, space_id: spaces[0]?.id || "" });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "", category: item.category || "decor", quantity: item.quantity || 1,
      space_id: item.space_id || "",
      sender_kind: item.sender_kind || "text",
      sender_label: item.sender_label || "",
      sender_ref_id: item.sender_ref_id || "",
      return_to: item.return_to || "",
      arrival_window: item.arrival_window || "",
      install_notes: item.install_notes || "",
      strike_notes: item.strike_notes || "",
      responsible_member_id: item.responsible_member_id || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    const name = (form.name || "").trim();
    if (!name) { toast.error("Name required"); return; }
    const body = {
      ...form,
      name,
      quantity: Math.max(1, Number(form.quantity) || 1),
      space_id: form.space_id || null,
      sender_ref_id: form.sender_kind === "text" ? null : (form.sender_ref_id || null),
      responsible_member_id: form.responsible_member_id || null,
    };
    try {
      if (editingId) {
        const { data } = await api.patch(`/events/${eventId}/setup-items/${editingId}`, body);
        setItems(items.map(x => x.id === editingId ? data : x));
        toast.success("Item updated");
      } else {
        const { data } = await api.post(`/events/${eventId}/setup-items`, body);
        setItems([...items, data]);
        toast.success("Item added");
      }
      setDialogOpen(false);
    } catch (e) {
      console.error("save item failed:", e);
      toast.error("Could not save item");
    }
  };

  const toggle = async (item, field) => {
    const next = !item[field];
    setItems(prev => prev.map(x => x.id === item.id ? { ...x, [field]: next } : x));
    try {
      await api.patch(`/events/${eventId}/setup-items/${item.id}`, { [field]: next });
    } catch (e) {
      // Revert on failure
      setItems(prev => prev.map(x => x.id === item.id ? { ...x, [field]: !next } : x));
      toast.error("Could not update");
    }
  };

  const removeItem = async (id) => {
    if (!confirm("Remove this item from setup & strike?")) return;
    try {
      await api.delete(`/events/${eventId}/setup-items/${id}`);
      setItems(items.filter(x => x.id !== id));
    } catch (e) { toast.error("Could not delete"); }
  };

  // --- Derivations ---
  const filtered = items.filter(i => (
    (filterCategory === "all" || i.category === filterCategory) &&
    (filterSpace === "all" || (i.space_id || "") === filterSpace)
  ));

  const setupProgress = { done: items.filter(i => i.installed).length, total: items.length };
  const strikeProgress = { done: items.filter(i => i.returned).length, total: items.length };

  const grouped = useMemo(() => {
    if (!groupBySpace) return { "": filtered };
    const g = {};
    for (const i of filtered) {
      const key = i.space_id || "__none__";
      (g[key] = g[key] || []).push(i);
    }
    return g;
  }, [filtered, groupBySpace]);

  const spaceLabel = (id) => {
    if (id === "__none__" || !id) return "Unassigned";
    return spaces.find(s => s.id === id)?.name || "Space";
  };

  if (loading) return <div className="text-sm text-muted">Loading setup plan…</div>;

  return (
    <div className="space-y-6" data-testid="setup-strike-root">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">MOD.LOGISTICS / {lane === "setup" ? "SETUP" : "STRIKE"}</div>
          <h2 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none">
            {lane === "setup"
              ? <><span className="font-mono tabular-nums">{setupProgress.done.toString().padStart(2, "0")}</span>/<span className="font-mono tabular-nums">{setupProgress.total.toString().padStart(2, "0")}</span> installed</>
              : <><span className="font-mono tabular-nums">{strikeProgress.done.toString().padStart(2, "0")}</span>/<span className="font-mono tabular-nums">{strikeProgress.total.toString().padStart(2, "0")}</span> returned</>}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-sm border border-hair p-0.5 bg-surface" data-testid="lane-toggle">
            <button
              onClick={() => setLane("setup")}
              data-testid="lane-setup"
              className={`px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1.5 ${lane === "setup" ? "bg-app text-[#F3EFE7]" : "text-muted hover:text-app"}`}
            >
              <PackageOpen size={12} strokeWidth={1.5} /> Setup
            </button>
            <button
              onClick={() => setLane("strike")}
              data-testid="lane-strike"
              className={`px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1.5 ${lane === "strike" ? "bg-app text-[#F3EFE7]" : "text-muted hover:text-app"}`}
            >
              <Truck size={12} strokeWidth={1.5} /> Strike
            </button>
          </div>
          {canEdit && (
            <Button
              onClick={openNew}
              data-testid="add-setup-item"
              className="rounded-sm bg-app hover:bg-[#000000] h-9 text-xs"
            >
              <Plus size={12} strokeWidth={1.5} className="mr-1.5" /> Add item
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bevel border-hair rounded-sm p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Filter size={11} strokeWidth={1.5} className="text-muted" />
          <span className="eyebrow">Filter</span>
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-40 h-8 rounded-sm text-xs" data-testid="filter-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {spaces.length > 0 && (
          <Select value={filterSpace} onValueChange={setFilterSpace}>
            <SelectTrigger className="w-40 h-8 rounded-sm text-xs" data-testid="filter-space">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All spaces</SelectItem>
              {spaces.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs text-muted">
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="group-by-space-toggle">
            <Checkbox checked={groupBySpace} onCheckedChange={v => setGroupBySpace(!!v)} />
            <LayoutGrid size={11} strokeWidth={1.5} /> Group by space
          </label>
        </div>
      </div>

      {/* Table(s) */}
      {items.length === 0 ? (
        <div className="bevel border-hair rounded-sm p-8 text-center">
          <ListChecks size={22} strokeWidth={1.2} className="text-muted mx-auto" />
          <div className="mt-3 font-serif text-2xl text-app">Nothing here yet</div>
          <p className="text-sm text-muted font-light mt-1 max-w-md mx-auto">
            Add every physical item arriving on site — decor, rentals, cake, AV, signage — so setup and strike stay accounted for.
          </p>
        </div>
      ) : (
        Object.keys(grouped).sort().map(key => {
          const rows = grouped[key] || [];
          if (rows.length === 0) return null;
          return (
            <div key={key || "flat"} className="space-y-2">
              {groupBySpace && (
                <div className="eyebrow flex items-center gap-1.5 pt-2" data-testid={`group-header-${key || "none"}`}>
                  <MapPin size={11} strokeWidth={1.5} /> {spaceLabel(key)}
                  <span className="text-muted font-normal">· {rows.length}</span>
                </div>
              )}
              <div className="bevel border-hair rounded-sm overflow-hidden">
                <table className="w-full text-sm" data-testid={`setup-table-${lane}`}>
                  <thead className="bg-canvas">
                    <tr className="text-left text-muted">
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium">Item</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium">Category</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium">Brought by</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium">
                        {lane === "setup" ? "Arrival" : "Return to"}
                      </th>
                      {lane === "setup" ? (
                        <>
                          <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium text-center">Arrived</th>
                          <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium text-center">Installed</th>
                        </>
                      ) : (
                        <>
                          <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium text-center">Packed</th>
                          <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium text-center">Loaded</th>
                          <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-medium text-center">Returned</th>
                        </>
                      )}
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(item => (
                      <Row
                        key={item.id}
                        item={item}
                        lane={lane}
                        vendors={vendors}
                        clients={clients}
                        canEdit={canEdit}
                        onToggle={toggle}
                        onEdit={openEdit}
                        onDelete={removeItem}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {/* Item dialog */}
      <ItemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        form={form}
        setForm={setForm}
        editingId={editingId}
        spaces={spaces}
        vendors={vendors}
        clients={clients}
        onSubmit={submit}
      />
    </div>
  );
}

// -------- Row --------
function Row({ item, lane, vendors, clients, canEdit, onToggle, onEdit, onDelete }) {
  const category = CATEGORIES.find(c => c.value === item.category);
  const senderText = renderSender(item, vendors, clients);
  const returnTo = item.return_to || item._return_to_hint || senderText;

  return (
    <tr className="border-t border-hair/60 hover:bg-canvas/50">
      <td className="px-3 py-2.5">
        <div className="text-app font-medium">
          {item.name}
          {item.quantity > 1 && <span className="text-muted text-xs ml-1.5">× {item.quantity}</span>}
        </div>
        {(lane === "setup" ? item.install_notes : item.strike_notes) && (
          <div className="text-[11px] text-muted font-light mt-0.5 truncate max-w-md">
            {lane === "setup" ? item.install_notes : item.strike_notes}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-muted text-xs">{category?.label || "—"}</td>
      <td className="px-3 py-2.5 text-muted text-xs">
        <div className="inline-flex items-center gap-1.5">
          {item.sender_kind === "vendor" && <Users size={10} strokeWidth={1.5} />}
          {item.sender_kind === "client" && <User size={10} strokeWidth={1.5} />}
          {senderText || "—"}
        </div>
      </td>
      <td className="px-3 py-2.5 text-muted text-xs">
        {lane === "setup"
          ? (item.arrival_window || "—")
          : (returnTo || "—")}
      </td>
      {lane === "setup" ? (
        <>
          <StatusCell item={item} field="arrived" canEdit={canEdit} onToggle={onToggle} testid="cell-arrived" />
          <StatusCell item={item} field="installed" canEdit={canEdit} onToggle={onToggle} testid="cell-installed" />
        </>
      ) : (
        <>
          <StatusCell item={item} field="packed" canEdit={canEdit} onToggle={onToggle} testid="cell-packed" />
          <StatusCell item={item} field="loaded" canEdit={canEdit} onToggle={onToggle} testid="cell-loaded" />
          <StatusCell item={item} field="returned" canEdit={canEdit} onToggle={onToggle} testid="cell-returned" />
        </>
      )}
      <td className="px-2 py-2.5">
        {canEdit && (
          <div className="flex items-center gap-1">
            <button onClick={() => onEdit(item)} data-testid={`edit-item-${item.id}`} className="text-muted hover:text-app p-1" title="Edit">
              <Pencil size={11} strokeWidth={1.5} />
            </button>
            <button onClick={() => onDelete(item.id)} data-testid={`delete-item-${item.id}`} className="text-muted hover:text-red-600 p-1" title="Delete">
              <Trash2 size={11} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function StatusCell({ item, field, canEdit, onToggle, testid }) {
  return (
    <td className="px-3 py-2.5 text-center">
      <label className={`inline-flex items-center justify-center ${canEdit ? "cursor-pointer" : ""}`}>
        <Checkbox
          checked={!!item[field]}
          onCheckedChange={() => canEdit && onToggle(item, field)}
          disabled={!canEdit}
          data-testid={`${testid}-${item.id}`}
        />
      </label>
    </td>
  );
}

function renderSender(item, vendors, clients) {
  const kind = item.sender_kind;
  if (kind === "vendor") {
    const v = vendors.find(m => m.id === item.sender_ref_id);
    return v ? (v.name || v.company || v.email) : (item.sender_label || "Vendor");
  }
  if (kind === "client") {
    const c = clients.find(x => x.id === item.sender_ref_id);
    return c ? c.name : (item.sender_label || "Client");
  }
  return item.sender_label || "";
}

// -------- Dialog --------
function ItemDialog({ open, onOpenChange, form, setForm, editingId, spaces, vendors, clients, onSubmit }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-app">
            {editingId ? "Edit item" : "New setup / strike item"}
          </DialogTitle>
          <DialogDescription>
            Anything physical arriving on site — decor, rentals, cake, mic packs, signage. Setup tracks install; strike tracks who packs it up and where it goes back.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label className="eyebrow block mb-1.5">Item name</Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="item-name" placeholder="Ceremony arch, 12 chargers, floral centerpiece…" />
          </div>
          <div>
            <Label className="eyebrow block mb-1.5">Category</Label>
            <Select value={form.category} onValueChange={v => set("category", v)}>
              <SelectTrigger className="rounded-sm" data-testid="item-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="eyebrow block mb-1.5">Quantity</Label>
            <Input type="number" value={form.quantity} onChange={e => set("quantity", e.target.value)} data-testid="item-quantity" />
          </div>
          <div>
            <Label className="eyebrow block mb-1.5">Space</Label>
            <Select value={form.space_id || "__none__"} onValueChange={v => set("space_id", v === "__none__" ? "" : v)}>
              <SelectTrigger className="rounded-sm" data-testid="item-space"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {spaces.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="eyebrow block mb-1.5">Arrival window</Label>
            <Input value={form.arrival_window} onChange={e => set("arrival_window", e.target.value)} data-testid="item-arrival" placeholder="Fri 3–5pm" />
          </div>

          <div className="col-span-2 pt-2 border-t border-hair">
            <Label className="eyebrow block mb-2">Brought by / sender</Label>
            <div className="flex items-center gap-2 mb-2" data-testid="sender-kind-toggle">
              {["text", "vendor", "client"].map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => set("sender_kind", k)}
                  data-testid={`sender-kind-${k}`}
                  className={`px-3 py-1.5 rounded-sm text-xs border transition-colors ${
                    form.sender_kind === k
                      ? "bg-app text-[#F3EFE7] border-[#0A0A0A]"
                      : "border-hair text-muted hover:border-[#0A0A0A]"
                  }`}
                >
                  {k === "text" ? "Free-text" : k === "vendor" ? "Vendor" : "Client"}
                </button>
              ))}
            </div>
            {form.sender_kind === "text" && (
              <Input value={form.sender_label} onChange={e => set("sender_label", e.target.value)} data-testid="sender-text" placeholder="e.g. Bride's mom, Willow Rentals" />
            )}
            {form.sender_kind === "vendor" && (
              <Select value={form.sender_ref_id || "__none__"} onValueChange={v => set("sender_ref_id", v === "__none__" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="sender-vendor"><SelectValue placeholder="Pick a vendor…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Pick a vendor —</SelectItem>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name || v.company || v.email}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {form.sender_kind === "client" && (
              <Select value={form.sender_ref_id || "__none__"} onValueChange={v => set("sender_ref_id", v === "__none__" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="sender-client"><SelectValue placeholder="Pick a client…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Pick a client —</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="col-span-2">
            <Label className="eyebrow block mb-1.5">Return to (override)</Label>
            <Input value={form.return_to} onChange={e => set("return_to", e.target.value)} data-testid="item-return-to" placeholder="Leave blank to auto-fill from vendor/client contact" />
          </div>

          <div className="col-span-2">
            <Label className="eyebrow block mb-1.5">Setup notes</Label>
            <Textarea rows={2} value={form.install_notes} onChange={e => set("install_notes", e.target.value)} data-testid="item-install-notes" placeholder="Where it goes, install order, cables, anything tricky…" />
          </div>
          <div className="col-span-2">
            <Label className="eyebrow block mb-1.5">Strike notes</Label>
            <Textarea rows={2} value={form.strike_notes} onChange={e => set("strike_notes", e.target.value)} data-testid="item-strike-notes" placeholder="Pack-out order, fragility, who's picking up…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="item-submit" onClick={onSubmit} className="rounded-sm bg-app hover:bg-[#000000]">
            {editingId ? "Save changes" : "Add item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
